import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { request } from 'shared/api/request'
import { IFsNodeSchema, IGetFsNodesSchema, NodeTypes } from '@cloudike/web_files'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { RootState } from 'shared/config/store'
import { getFilesSdk } from 'shared/sdk/files'
import { assetsActions, assetsSelectors } from 'features/assets/model/slice/assetsSlice'
import {
  boardsActions,
  boardsSelectors,
  changeBoardTagsThunk,
  updateSystemFieldThunk
} from 'features/boards/model/slice/boardsSlice'
import { AUTH_MODAL_MODE, setAuthModalState } from 'features/common/model/slice/appSlice'
import { UPLOADER_LIST_TYPES, uploadSharedFilesThunk } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { initSharedWs } from 'shared/websocket/ws'

import { publicBoardsApi } from '../../../../shared/api/publicBoardsApi'

export enum FilesPublicLinksErrorTypes {
  NOT_EXIST = 'NOT_EXIST',
  EMPTY_FOLDER = 'EMPTY_FOLDER',
  SOMETHING_WENT_WRONG = 'SOMETHING_WENT_WRONG',
  NO_ACCESS = 'NO_ACCESS',
}

export enum ReplaceModalTypes {
  REPLACE = 'replace',
  KEEP_BOTH = 'keep_both'
}

export enum CheckboxVisibilityType {
  VISIBLE = 'visible',
  HIDDEN = 'hidden'
}

const adapter = createEntityAdapter<IFsNodeSchema>({
  sortComparer: (a, b) => {
    return a.name.localeCompare(b.name)
  }
})
interface State {
  status: LOADING_STATUSES,
  totalItemsCount: number,
  passwordModalOpened: boolean,
  error: null | FilesPublicLinksErrorTypes
  nodeData: null | IFsNodeSchema,
  rootNodeType: NodeTypes,
  notMyResourseHref: string,
  itemsHref: string,
  idsHref: string,
  nodeHref: string,
  copyDirHref: string,
  token: null | string,
  sharedId: null | string,
  permission: null | string,
  isSizeQuotaExceedError: boolean,
  nodeFieldsById: Record<string, any>,
  systemFields: any,
  needAuthInPublicLink: boolean,
  breadcrumbs: { name: string, id?: string }[],
  currentFolder: null | any,
  currentBoardId: null | string,
  systemFieldsValues: {
    statusOptions: any[],
    tags: any,
  },
  path: []
}

export const fetchCurrentBoardDataThunk = createAsyncThunk('publicLinkFiles/fetchCurrentBoardData',
  async function ({ id }: { id: string }, { getState }) {
    const state = getState() as RootState
    const { publicLinksService } = getFilesSdk()
    const nodeHref = state.publicLinkFiles.nodeHref
    if(!nodeHref) return
    return await publicLinksService.getNotMyFsNodeByLink(state.publicLinkFiles.nodeHref, id)
  })

const getChildrenHref = async (nodeData: any, id: string | undefined, dispatch: any): Promise<string | undefined> => {
  if (!id) {
    return nodeData?._links?.root_dir?.href
  }

  try {
    const currentBoard = await dispatch(fetchCurrentBoardDataThunk({ id })).unwrap()
    if (!currentBoard?.data?._links?.children?.href) {
      throw new Error('Invalid board data or missing children link')
    }
    return currentBoard.data._links.children.href
  } catch (error) {
    console.error('Failed to fetch current board data:', error)
    throw new Error('Failed to fetch board data')
  }
}

const isValidDirectory = (item: IFsNodeSchema): boolean => {
  return item && item.id && item.type === NodeTypes.DIR
}

const isValidFile = (item: IFsNodeSchema): boolean => {
  return item && item.id && (item.type === NodeTypes.FILE || (item.type as any) === 'link')
}

const processNodesResponse = (nodesResponse: IFsNodeSchema[], dispatch: any): IFsNodeSchema[] => {
  if (!Array.isArray(nodesResponse)) {
    console.warn('Invalid API response: expected array, got:', typeof nodesResponse)
    throw new Error('Invalid server response')
  }

  if (nodesResponse.length === 0) {
    dispatch(boardsActions.setAll([]))
    dispatch(assetsActions.setAllAssets([]))
    return []
  }

  const directories = nodesResponse.filter(isValidDirectory)
  const files = nodesResponse.filter(isValidFile)

  dispatch(actions.setError(null))
  dispatch(boardsActions.setAll(directories))
  dispatch(assetsActions.setAllAssets(files))

  return nodesResponse
}

const validateSdkAndNodeData = (sdk: any): void => {
  if (!sdk?.publicLinksService) {
    throw new Error('Public links service is not available')
  }
}

const buildApiParams = (id?: string): IGetFsNodesSchema => {
  const apiParams: IGetFsNodesSchema = {
    preview: true,
    preview_jwt: true,
    embedded: true
  }

  if (id) {
    apiParams.parent_id = id
  }

  return apiParams
}

const handleFetchError = (error: any, dispatch: any, rejectWithValue: any): any => {
  console.error('Unexpected error in fetchFilesPublicLinkItems:', error)
  dispatch(actions.setError(FilesPublicLinksErrorTypes.SOMETHING_WENT_WRONG))

  if (error.message) {
    return rejectWithValue(error.message)
  }
  return rejectWithValue('An unexpected error occurred')
}

export const fetchFilesPublicLinkItems = createAsyncThunk(
  'publicLinkFiles/fetchFilesPublicLinkItems',
  async function ({ id }: { id?: string, isUserInFamily?: boolean }, { dispatch, getState, rejectWithValue }) {
    try {
      const state = getState() as RootState
      const { token, nodeData } = state.publicLinkFiles
      const sdk = getFilesSdk()

      validateSdkAndNodeData(sdk)

      const childrenHref = await getChildrenHref(nodeData, id, dispatch)
      if (!childrenHref) {
        return rejectWithValue('Children href is required for API request')
      }

      const apiParams = buildApiParams(id)

      const nodesResponse = await sdk.publicLinksService.getAllNotMyFsNodesByLink(
        childrenHref, apiParams, null, null, token
      )

      return processNodesResponse(nodesResponse, dispatch)

    } catch (error) {
      return handleFetchError(error, dispatch, rejectWithValue)
    }
  }
)

async function getNodeUrlTmplBySharedId(publicLinksService, sharedId) {
  const rsp = await publicLinksService.getShareWithMeRootById(sharedId)
  return rsp.data?._links?.node?.href
}

export const removeAssetsPublicLinkThunk = createAsyncThunk(
  'publicLinkFiles/removeAssetsPublicLinkThunk',
  async function ({ ids }: { ids: string[] }, { dispatch, getState }) {
    const state = getState() as RootState

    const sharedId = state.publicLinkFiles.nodeData.id
    const sdk = getFilesSdk()
    const { publicLinksService } = sdk
    const params = {
      is_trashed: true,
    }

    try {
      const urlTmpl = await getNodeUrlTmplBySharedId(publicLinksService, sharedId)

      const results = await Promise.allSettled(ids.map(async (id) => {
        return await publicLinksService.updateNotMyFsNodeByLink(urlTmpl, id, params)
      }))

      const successfulIds = results.map(result => result.status === 'fulfilled' ? result.value.data.id : null).filter(Boolean)

      dispatch(assetsActions.deleteItems(successfulIds))
      dispatch(actions.deleteItems(successfulIds))
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Failed to remove items',
        message: 'Please try again',
      })
    } finally {
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
    }
  }
)

export const moveItemsPublicLinkThunk = createAsyncThunk(
  'publicLinkFiles/moveItemsPublicLinkThunk',
  async function ({ destinationId }: { destinationId: string }, { dispatch, getState }) {
    const state = getState() as RootState

    const sharedId = state.publicLinkFiles.nodeData?.id ?? ''
    const sdk = getFilesSdk()
    const { publicLinksService } = sdk
    const items = state.modal.data?.items || []

    try {
      const urlTmpl = await getNodeUrlTmplBySharedId(publicLinksService, sharedId)

      const results = await Promise.allSettled(items.map(async (item) => {
        return await publicLinksService.updateNotMyFsNodeByLink(urlTmpl, item.id, { parent_id: destinationId === 'root' ? '' : destinationId, name: item.name })
      }))

      const resultsMap = new Map()
      const successfulOperations = []
      const failedOperations = []

      results.forEach((result, index) => {
        const item = items[index]
        if (result.status === 'fulfilled') {
          resultsMap.set(item.id, result.value)
          successfulOperations.push(item)
        } else {
          failedOperations.push(item)
        }
      })

      const successfulIds = successfulOperations.map(item => {
        const result = resultsMap.get(item.id)
        if (result && result.data.parent_id !== item.parent_id) {
          return { id: item.id, type: item.type }
        }
        return null
      }).filter(Boolean)

      if (successfulIds.length > 0) {
        dispatch(assetsActions.deleteItems(successfulIds.filter((item: any) => item.type === NodeTypes.FILE || item.type === 'link').map((item: any) => item.id)))
        dispatch(boardsActions.deleteItems(successfulIds.filter(item => item.type === NodeTypes.DIR).map(item => item.id)))
        dispatch(actions.deleteItems(successfulIds.map(item => item.id)))
      }

      if (successfulOperations.length > 0 && successfulIds.length > 0 && failedOperations.length === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: 'Items moved',
          message: 'Items moved successfully',
        })
      } else if (successfulOperations.length > 0 && successfulIds.length > 0 && failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Partial success',
          message: `Moved ${successfulOperations.length} items, failed to move ${failedOperations.length} items`,
        })
      } else if (failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Failed to move items',
          message: 'Please try again',
        })
      }
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Failed to remove items',
        message: 'Please try again',
      })
    } finally {
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
    }
  }
)

export const addToAnotherBoardItemsPublicLinkThunk = createAsyncThunk(
  'publicLinkFiles/addToAnotherBoardItemsPublicLinkThunk',
  async function ({ destinationId }: { destinationId: string }, { dispatch, getState }) {
    const state = getState() as RootState
    const sharedId = state.publicLinkFiles.nodeData?.id ?? ''
    const sdk = getFilesSdk()
    const { publicLinksService } = sdk
    const items = state.modal.data?.items || []

    try {
      const parentId = destinationId === 'root' ? '' : destinationId

      const results = await Promise.allSettled(items.map(async (item) => {
        const linkedNodeId = item.id
        // There is no access to the original files on the public board.
        // That is why you should use id of the 'link' as 'linked_node_id'
        return await publicLinksService.copySharedWithMeByShareId(item.id, parentId, sharedId, { type: 'link', linked_node_id: linkedNodeId } as any)
      }))

      const successfulOperations = []
      const failedOperations = []

      results.forEach((result, index) => {
        const item = items[index]
        if (result.status === 'fulfilled') {
          successfulOperations.push({ item, result: result.value })
        } else {
          failedOperations.push(item)
        }
      })

      const isCurrentBoard = items.some(item => item.parent_id === parentId)

      if (isCurrentBoard && successfulOperations.length > 0) {
        successfulOperations.forEach(({ result }) => {
          dispatch(assetsActions.addAsset(result.data))
          dispatch(actions.addOne(result.data))
        })
      }

      if (successfulOperations.length > 0 && failedOperations.length === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: 'Items added to another board',
          message: 'Items added successfully',
        })
      } else if (successfulOperations.length > 0 && failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Partial success',
          message: `Added ${successfulOperations.length} items, failed to add ${failedOperations.length} items`,
        })
      } else if (failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Failed to add items',
          message: 'Please try again',
        })
      }

    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Failed to remove items',
        message: 'Please try again',
      })
    } finally {
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
    }
  }
)

export const copyBoardsPublicLinkThunk = createAsyncThunk(
  'publicLinkFiles/copyBoardsPublicLinkThunk',
  async function ({ destinationId }: { destinationId: string }, { dispatch, getState }) {
    const state = getState() as RootState
    const sdk = getFilesSdk()
    const { publicLinksService } = sdk
    const copyDirHref = state.publicLinkFiles.copyDirHref
    const token = state.publicLinkFiles.token
    const items = state.modal.data?.items || []

    try {
      const parentId = destinationId === 'root' ? '' : destinationId

      const dirItems = items.filter((item) => item.type === NodeTypes.DIR)

      const results = await Promise.allSettled(dirItems.map(async (item) => {
        const urlTmpl = await getNodeUrlTmplBySharedId(publicLinksService, state.publicLinkFiles.sharedId as string)
        const { data: { _links: { self: { href: srcUrl } } } } = await publicLinksService.getNotMyFsNodeByLink(urlTmpl, item.id, token as any)
        const params = { source: srcUrl, destination: { parent_id: parentId } }
        return await publicLinksService.copySharedWithMeDir(copyDirHref, params, token)
      }))

      const successfulOperations = [] as any[]
      const failedOperations = [] as any[]

      results.forEach((result, index) => {
        const item = dirItems[index]
        if (result.status === 'fulfilled') {
          successfulOperations.push({ item, result: result.value })
        } else {
          failedOperations.push(item)
        }
      })

      if (successfulOperations.length > 0 && failedOperations.length === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: `${dirItems.length} ${dirItems.length === 1 ? 'item' : 'items'} copied`,
          message: 'Items copied successfully',
        })
      } else if (successfulOperations.length > 0 && failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Partial success',
          message: `Copied ${successfulOperations.length} items, failed to copy ${failedOperations.length} items`,
        })
      } else if (failedOperations.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Something went wrong',
          message: 'Please try again',
        })
      }
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
      throw new Error('Something went wrong with copying boards via public link')
    } finally {
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
    }
  }
)

export const renamePublicLinkFileThunk = createAsyncThunk(
  'publicLinkFiles/renamePublicLinkFileThunk',
  async function ({ id, name, type, callback }: { id: string, name: string, type: string, callback?: () => void }, { dispatch, getState }) {
    const state = getState() as RootState
    const items = type === 'asset' ? assetsSelectors.selectAll(state.assets) : boardsSelectors.selectAll(state.boards)

    const item = items.find((item) => item.id === id)

    const finalName = name.trim()

    const unselectAll = () => {
      if (type === 'asset') {
        dispatch(assetsActions.setRenamingItemId(null))
        dispatch(assetsActions.unselectAll())
      } else {
        dispatch(boardsActions.setRenamingItemId(null))
        dispatch(boardsActions.unselectAll())
      }
    }

    if (!finalName) {
      unselectAll()

      return
    }

    if (finalName.length > 255) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'File name is too long',
        message: 'Please try again',
      })

      unselectAll()

      return
    }

    if (name === item?.name) {
      unselectAll()

      return
    }

    try {
      const sharedId = state.publicLinkFiles.nodeData.id
      const sdk = getFilesSdk()
      const { publicLinksService } = sdk
      const params = {
        name,
      }

      const urlTmpl = await getNodeUrlTmplBySharedId(publicLinksService, sharedId)

      await publicLinksService.updateNotMyFsNodeByLink(urlTmpl,id, params)

      dispatch(actions.updateItem({ id: id, name: finalName }))

      if (type === 'asset') {
        dispatch(assetsActions.updateAsset({ id: id, name: finalName }))
      } else {
        dispatch(boardsActions.updateItem({ id: id, name: finalName }))
      }

      if (callback) {
        callback()
      }

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: type === 'asset' ? 'Asset renamed' : 'Board renamed',
        message: type === 'asset' ? 'Asset renamed successfully' : 'Board renamed successfully',
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    } finally {
      unselectAll()
    }
  }
)

export const fetchFilesPublicLinkBreadcrumbsThunk = createAsyncThunk(
  'publicLinkFiles/fetchFilesPublicLinkBreadcrumbsThunk',
  async function ({ paths }: { paths: string[] }, { getState, dispatch }) {
    const state = getState() as RootState
    const nodeHref = state.publicLinkFiles.nodeHref
    const sharedId = state.publicLinkFiles.sharedId
    const breadcrumbs = []

    for (const path of paths.slice(1)) {
      try {
        const response = await getFilesSdk().publicLinksService.getNotMyFsNodeByLink(nodeHref, path)
        const name = response.data.name

        if (name) {
          breadcrumbs.push({ name, id: path, data: response.data })
        }
      } catch (error) {
        console.error(`Failed to fetch name for path ${path}`, error)
      }
    }

    const finalBreadcrumbs = [{ name: 'All files', id: sharedId }, ...breadcrumbs]

    dispatch(actions.setBreadcrumbs(finalBreadcrumbs))
  }
)

export const fetchFilesPublicLinkInfoThunk = createAsyncThunk(
  "publicLinkFiles/fetchFilesPublicLinkInfoThunk",
  async function ({ shareId }: { shareId: string }, { dispatch, getState }) {
    try {
      const state = getState() as RootState
      const { publicLinksService } = getFilesSdk()
      const userId = state.user.user?.id ?? 0
      const isAuth = state.user.token

      dispatch(actions.setNodeData(null))
      dispatch(actions.setSharedId(shareId))
      const response = await publicLinksService.getPublicShare(shareId, userId)
      const data = response.data

      const myResource = data?._links?.my_resource?.href
      const notMyResource = data?._links?.not_my_resource?.href
      const accessType = data.access_type

      if (myResource) {
        const myResourceResponse: any = await request("GET", myResource, {}, { host: null })
        dispatch(actions.setNodeData(myResourceResponse))
        dispatch(actions.setRootNodeType(myResourceResponse.type))
        return
      }

      if (notMyResource) {
        const notMyResourceResponse: any = await request("GET", notMyResource, {}, { host: null })
        dispatch(actions.setNotMyResourceHref(notMyResource))
        dispatch(actions.setPermission(notMyResourceResponse?._embedded?.share?.permission))
        dispatch(actions.setNodeData(notMyResourceResponse))
        dispatch(actions.setRootNodeType(notMyResourceResponse.type))

        const shareResponse = await publicLinksService.getShareWithMeRootById(shareId, {
          preview: true,
          preview_jwt: true,
        })

        const href = shareResponse.data._links.nodes.href
        const nodeHref = shareResponse.data._links.node.href
        const idsHref = shareResponse.data._links.ids.href
        const copyItemsHref = shareResponse.data._links.dir_copy_tasks.href

        if ((notMyResourceResponse as any).type === NodeTypes.FILE || (notMyResourceResponse as any).type === 'link') {
          dispatch(actions.setNodeData(shareResponse.data))
        }

        dispatch(actions.setItemsHref(href))
        dispatch(actions.setItemsIdsHref(idsHref))
        dispatch(actions.setNodeHref(nodeHref))
        dispatch(actions.setCopyDirHref(copyItemsHref))

        return
      }

      if (accessType === "collaborators") {
        if(!isAuth) {
          dispatch(setAuthModalState({ isOpen: true, type: 'overlay', inPublicLink: true }))
        } else {
          dispatch(actions.setError(FilesPublicLinksErrorTypes.NO_ACCESS))
        }
        return
      }

      dispatch(actions.setError(FilesPublicLinksErrorTypes.NO_ACCESS))
    } catch (error) {
      if (error.code === "Unauthorized") {
        dispatch(setAuthModalState({ isOpen: true, type: 'overlay', mode: AUTH_MODAL_MODE.SIGNIN }))
        return
      }

      if (error.code === "Forbidden") {
        dispatch(actions.setError(FilesPublicLinksErrorTypes.NO_ACCESS))
        return
      }

      if (error.code === "NotFound") {
        dispatch(actions.setError(FilesPublicLinksErrorTypes.NOT_EXIST))
        return
      }

      dispatch(actions.setError(FilesPublicLinksErrorTypes.SOMETHING_WENT_WRONG))
    }
  }
)

export const fetchNodeFieldsThunk = createAsyncThunk(
  'publicLinkFiles/fetchNodeFieldsThunk',
  async function ({ node }: { node: any }, { dispatch }) {
    const nodeId = node?.node_id || node?.id
    if (!nodeId) {
      return
    }

    dispatch(actions.clearNodeFieldsForNode(nodeId))

    let fieldsHref: string

    if (node?._links?.fields) {
      fieldsHref = node?._links?.fields?.href
    } else {
      const nodeLink = publicBoardsApi.getNodeLink(node)
      const nodeHrefResponse = await publicBoardsApi.getNodeData(nodeLink)

      fieldsHref = nodeHrefResponse?._links?.fields?.href
    }

    if (!fieldsHref) {
      return
    }

    const fieldsResponse: any = await publicBoardsApi.getFieldsData(fieldsHref)
    const { _embedded:{ fields } } = fieldsResponse
    const statusField = fieldsResponse._embedded.fields.find((field: any) => field?.field?.title === 'status') as any
    const tagsField = fieldsResponse._embedded.fields.find((field: any) => field?.field?.title === 'tags') as any
    const descriptionField = fieldsResponse._embedded.fields.find((field: any) => field?.field?.title === 'description') as any
    const statusOptions = statusField?.field?.options || []
    const tagsOptions = tagsField?.field?.options || []
    dispatch(actions.setNodeFieldsForNode({ nodeId, fields: { status: statusField, tags: tagsField || [], description: descriptionField || null } }))
    dispatch(actions.setSystemFields(fields))
    dispatch(actions.setSystemFieldsValues({ statusOptions, tags: tagsOptions || [] }))
  }
)

export const createNewSystemTagThunk = createAsyncThunk(
  'publicLinkFiles/createNewSystemTagThunk',
  async ({ currentBoardData, fieldID, systemTags, boardTags, newTagValue, nodeId, signal }:
    { currentBoardData: any, fieldID: string, systemTags: any, boardTags: any, newTagValue: any, nodeId: any, signal: any }, { dispatch }) => {
    const data = { options: [...systemTags, { value: newTagValue }] }
    const rsp = await dispatch(updateSystemFieldThunk({ fieldID, data, signal })).unwrap()
    const { options } = rsp
    const newSysTag = options.find((tag) => tag.value === newTagValue)
    const newBoardTagsIds = [...boardTags, newSysTag.id ]
    await dispatch(changeBoardTagsThunk({ nodeId, tagsFieldID: fieldID, data: { value: newBoardTagsIds }, signal }))
    dispatch(fetchNodeFieldsThunk({ node: currentBoardData }))
  }
)


export const downloadSharedFilesArchiveThunk = createAsyncThunk(
  'publicLinkFiles/downloadSharedFilesArchiveThunk',
  async function ({ items }: { items: any[] }, { getState }) {
    try {
      const state = (getState() as RootState)
      const publicLinkFilesState = state.publicLinkFiles
      const nodeData = publicLinkFilesState.nodeData as any
      const nodesDownloadsHref = nodeData._links.nodes_downloads.href
      const currentAssetsItems = assetsSelectors.selectAll(state.assets)
      const currentBoardsItems = boardsSelectors.selectAll(state.boards)
      const currentItems = [...currentAssetsItems, ...currentBoardsItems]

      const itemsToDownload = items || currentItems
      const itemsToDownloadIds = itemsToDownload.map(item => item.id)
      const token = publicLinkFilesState.token
      const shareId = publicLinkFilesState.sharedId

      if (itemsToDownload.length === 1) {
        const itemToDownload = itemsToDownload[0]

        await downloadSingleFileOrFolder(shareId, itemToDownload, nodesDownloadsHref, token)
      } else {
        await downloadMultipleItemsAsZip(token, itemsToDownloadIds, nodesDownloadsHref)
      }
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Try again'
      })
    } finally {
    }

    async function downloadSingleFileOrFolder(
      shareId: string,
      itemToDownload: IFsNodeSchema,
      nodesDownloadsHref: string,
      token?: string) {
      const downloadLink = await getDownloadLink(shareId, itemToDownload, nodesDownloadsHref, token)

      if (downloadLink) {
        window.location.href = downloadLink
      }
    }


    async function getDownloadLink(
      shareId: string,
      itemToDownload: IFsNodeSchema,
      nodesDownloadsHref: string,
      token: string
    ): Promise<string> {
      const nodeType = itemToDownload.type
      const filesSdk = getFilesSdk().publicLinksService


      let id = itemToDownload.id

      if (shareId === id) {
        id = itemToDownload.node_id
      }

      if (nodeType === NodeTypes.FILE || (nodeType as unknown as string) === 'link') {
        return filesSdk.getDownloadLink(shareId, id, token)
      }

      if (nodeType === NodeTypes.DIR) {
        const { data: { _links: { zip_stream: { href } } } } = await filesSdk
          .createSharedWithMeNodesZipStream(nodesDownloadsHref, [id], token)
        return href
      }

    }

    async function downloadMultipleItemsAsZip(token: string, itemsToDownloadIds: string[], nodesDownloadsHref: string) {
      const { data: { _links: { zip_stream: { href } } } } =
        await getFilesSdk()
          .publicLinksService
          .createSharedWithMeNodesZipStream(nodesDownloadsHref, itemsToDownloadIds, token)
      window.location.href = href
    }
  }
)

export const loadPublicLinkAssetsInBackgroundThunk = createAsyncThunk(
  'publicLinkFiles/loadPublicLinkAssetsInBackgroundThunk',
  async ({ currentFileId, callback }: { currentFileId: string, callback: (items: any[], currentIndex: number) => void }, { getState, dispatch }) => {
    const state = getState() as RootState
    const currentFolderId = (state.publicLinkFiles as any).currentFolderId
    const sharedId = state.publicLinkFiles.sharedId
    const folderId = currentFolderId === sharedId ? undefined : currentFolderId

    try {
      await dispatch(fetchFilesPublicLinkItems({ id: folderId })).unwrap()

      const updatedState = getState() as RootState
      const assets = assetsSelectors.selectAll(updatedState.assets)

      if (assets.length > 1) {
        const currentIndex = assets.findIndex(asset => asset.id === currentFileId)
        if (currentIndex !== -1) {
          callback(assets, currentIndex)
        }
      }
    } catch (error) {
      console.error('Background loading failed:', error)
    }
  }
)

export const checkAndUploadSharedFilesThunk = createAsyncThunk(
  'publicLinkFiles/checkAndUploadSharedFilesThunk',
  async ({ files, callback, modificators, uploadingSourceType }: {
      files: FileList, callback?: () => void,
      modificators?: any[], uploadingSourceType?: UPLOADER_LIST_TYPES }, { getState, dispatch }) => {
    const state = getState() as RootState
    const { currentBoardId } = state.publicLinkFiles
    const sharedId = state.publicLinkFiles.sharedId

    try {
      dispatch(uploadSharedFilesThunk({ files, callback, parentId: currentBoardId, sharedId, modificators, uploadingSourceType }))
    } catch (error) {
      console.log(error)
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again'
      })
    } finally {
    }
  })

export const updateBoardDescriptionThunk = createAsyncThunk(
  "publicLinkFiles/updateBoardDescriptionThunk",
  async (
    { userId, nodeId, shareId, fieldID, description }:
    { userId: number, nodeId: string, shareId: string, fieldID: string | null, description: string },
    { dispatch, getState }
  ) => {
    let newValue = description

    if (fieldID) {
      // Update description as custom field
      const response = await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, { value: description })
      newValue = response?.value ?? description
    } else {
      await publicBoardsApi.updateBoardData(userId, shareId, nodeId, { description })
    }

    const state = getState() as RootState
    const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]

    if (currentNodeFields) {
      const updated = {
        ...currentNodeFields,
        description: {
          ...currentNodeFields.description,
          field: currentNodeFields.description?.field ?? (fieldID ? { id: fieldID } : currentNodeFields.description?.field),
          value: newValue,
        }
      }

      dispatch(actions.setNodeFieldsForNode({ nodeId, fields: updated }))
    }

    dispatch(actions.updateItem({ id: nodeId, description: newValue }))
  }
)

export const updateBoardNameThunk = createAsyncThunk(
  "publicLinkFiles/updateBoardNameThunk",
  async ({ userId, nodeId, shareId, name }: { userId: number, nodeId: string, shareId: string, name: string }, { dispatch }) => {
    await publicBoardsApi.updateBoardData(userId, shareId, nodeId, { name })
    dispatch(actions.updateItem({ id: nodeId, name }))
  }
)

export const changePublicBoardTagsThunk = createAsyncThunk(
  'publicLinkFiles/changePublicBoardTagsThunk',
  async (
    { userId, shareId, nodeId, fieldID, data, signal }:
    { userId: number, shareId: string, nodeId: string, fieldID: string, data: any, signal?: AbortSignal },
    { getState, dispatch }
  ) => {
    const rsp = await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, data, signal)
    const newValue = rsp?.value
    const state = getState() as RootState
    const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
    if (currentNodeFields) {
      const updated = {
        ...currentNodeFields,
        tags: {
          ...currentNodeFields.tags,
          value: newValue ?? data?.value ?? currentNodeFields.tags?.value ?? []
        }
      }
      dispatch(actions.setNodeFieldsForNode({ nodeId, fields: updated }))
    }
  }
)

export const changePublicBoardStatusThunk = createAsyncThunk(
  'publicLinkFiles/changePublicBoardStatusThunk',
  async (
    { userId, shareId, nodeId, fieldID, statusId, signal }:
    { userId: number, shareId: string, nodeId: string, fieldID: string, statusId: string | null, signal?: AbortSignal },
    { getState, dispatch }
  ) => {
    if (statusId) {
      const rsp = await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, { value: statusId }, signal)
      const newValue = rsp?.value ?? statusId
      const state = getState() as RootState
      const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
      if (currentNodeFields) {
        const updated = {
          ...currentNodeFields,
          status: {
            ...currentNodeFields.status,
            value: newValue
          }
        }
        dispatch(actions.setNodeFieldsForNode({ nodeId, fields: updated }))
      }
    } else {
      await publicBoardsApi.deleteBoardFields(userId, shareId, nodeId, fieldID, signal)
      const state = getState() as RootState
      const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
      if (currentNodeFields) {
        const updated = {
          ...currentNodeFields,
          status: {
            ...currentNodeFields.status,
            value: null
          }
        }
        dispatch(actions.setNodeFieldsForNode({ nodeId, fields: updated }))
      }
    }
  }
)

export const publicLinkFilesSlice = createSlice({
  name: 'publicLinkFiles',
  initialState: adapter.getInitialState<State>({
    status: LOADING_STATUSES.LOADING,
    totalItemsCount: 0,
    passwordModalOpened: false,
    error: null,
    nodeData: null,
    rootNodeType: NodeTypes.DIR,
    token: null,
    sharedId: null,
    permission: null,
    notMyResourseHref: null,
    itemsHref: null,
    idsHref: null,
    nodeHref: null,
    copyDirHref: null,
    isSizeQuotaExceedError: null,
    nodeFieldsById: {},
    needAuthInPublicLink: false,
    breadcrumbs: [],
    currentFolder: null,
    currentBoardId: null,
    systemFieldsValues: {
      statusOptions: [] as any[],
      tags: [] as any,
    },
    systemFields: null,
    path: []
  }),
  reducers: {
    setNeedAuthInPublicLink: (state, action) => {
      state.needAuthInPublicLink = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setIsSizeQuotaExceedError: (state, action) => {
      state.isSizeQuotaExceedError = action.payload
    },
    setItems: (state, action) => {
      adapter.setAll(state, action.payload)
      state.status = LOADING_STATUSES.SUCCEEDED
    },
    setTotalCount: (state, action) => {
      state.totalItemsCount = action.payload
    },
    setNodeData: (state, action) => {
      state.nodeData = action.payload
      if (!action.payload) {
        state.nodeFieldsById = {}
      }
    },
    setRootNodeType: (state, action) => {
      state.rootNodeType = action.payload
    },
    removeItems: (state, action) => {
      adapter.removeMany(state, action.payload)
    },
    setToken: (state, action) => {
      state.token = action.payload
    },
    setSharedId: (state, action) => {
      state.sharedId = action.payload
    },
    setPermission: (state, action) => {
      state.permission = action.payload
    },
    setNotMyResourceHref: (state, action) => {
      state.notMyResourseHref = action.payload
    },
    setItemsHref: (state, action) => {
      state.itemsHref = action.payload
    },
    setItemsIdsHref: (state, action) => {
      state.idsHref = action.payload
    },
    setNodeHref: (state, action) => {
      state.nodeHref = action.payload
    },
    setCopyDirHref: (state, action) => {
      state.copyDirHref = action.payload
    },
    setNodeFieldsForNode: (state, action: PayloadAction<{ nodeId: string, fields: any }>) => {
      const { nodeId, fields } = action.payload
      if (!nodeId) return
      state.nodeFieldsById[nodeId] = fields
    },
    clearNodeFieldsForNode: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload
      if (!nodeId) return
      delete state.nodeFieldsById[nodeId]
    },
    setSystemFields: (state, action) => {
      state.systemFields = action.payload
    },
    addItem: (state, action) => {
      adapter.removeOne(state, action.payload.id)
      adapter.addOne(state, action.payload)
    },
    addOne: (state, action) => {
      adapter.addOne(state, action.payload)
    },
    updateItem: (state, action) => {
      adapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      })
    },
    updateManyItems: (state, action) => {
      adapter.updateMany(state, action.payload)
    },
    setAll: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    setCurrentBoardId: (state, action) => {
      state.currentBoardId = action.payload
    },
    deleteItems: (state, action) => {
      adapter.removeMany(state, action.payload)
    },
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload
    },
    setSystemFieldsValues: (state, action: PayloadAction<{ statusOptions: any[] |  null, tags: string[] | string | null }>) => {
      state.systemFieldsValues = action.payload
    },
    updateSystemFieldsValues: (state, action: PayloadAction<{ status?: string[] | string | null, tags?: string[] | string | null }>) => {
      state.systemFieldsValues = {
        ...state.systemFieldsValues,
        ...action.payload,
      }
    },
    setPath: (state, action) => {
      state.path = action.payload
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchFilesPublicLinkItems.fulfilled, (state) => {
        state.status = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchFilesPublicLinkItems.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })
      .addCase(fetchFilesPublicLinkItems.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING
      })
  }
})

const { reducer, actions } = publicLinkFilesSlice

export { actions as publicLinkFilesActions, reducer as publicLinkFilesReducer }
